import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Spinner } from './EnvLayout';
import { ui, errText, QTYPE_LABEL, type QType } from './envUi';

interface Row { id: number; en: string; ar: string; type: string; visible: boolean }

/** Survey questions: the random pool, the closing questions, and how many are drawn. */
export default function AdminSurvey() {
  const utils = trpc.useUtils();
  const { data, isLoading, error } = trpc.questionnaire.adminQuestions.useQuery();
  const refresh = () => { utils.questionnaire.adminQuestions.invalidate(); utils.questionnaire.questions.invalidate(); };

  const setCount = trpc.questionnaire.setRandomCount.useMutation({ onSuccess: refresh });
  const [count, setCountDraft] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (error || !data) return <p className="text-red-600 dark:text-red-400">{error ? errText(error) : 'Could not load questions.'}</p>;

  const shown = count ?? String(data.randomCount);
  const countNum = Number(shown);
  const countOk = shown.trim() !== '' && Number.isInteger(countNum) && countNum >= 0 && countNum <= 100;
  const visibleStatements = data.statements.filter(s => s.visible).length;

  return (
    <div className="flex flex-col gap-10">
      {/* How many random questions */}
      <section className={`${ui.card} p-4`}>
        <h2 className="mb-1 font-bold">Questions per participant</h2>
        <p className={`${ui.muted} mb-3`}>
          Each participant gets this many questions drawn at random from the pool below, followed by the closing questions.
          Right now the pool has {visibleStatements} visible statement{visibleStatements === 1 ? '' : 's'}.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="number" min={0} max={100} className={`${ui.input} !w-24`} value={shown} aria-label="Random questions per participant"
                 onChange={e => setCountDraft(e.target.value)} />
          <button className={ui.btn} disabled={!countOk || count === null || setCount.isPending}
                  onClick={() => setCount.mutate({ count: countNum }, { onSuccess: () => setCountDraft(null) })}>
            Save
          </button>
          {setCount.error && <span className="text-sm text-red-600 dark:text-red-400">{errText(setCount.error)}</span>}
        </div>
      </section>

      <QuestionList
        title="Statements (random pool)"
        help="Agree / Disagree statements. Hide one to keep it without asking it."
        kind="statement"
        rows={data.statements}
        onChanged={refresh}
      />

      <QuestionList
        title="Closing questions"
        help={'Always asked after the random statements, in this order. Hide the ones you don\'t want, or remove them all to end the survey after the statements.'}
        kind="closing"
        rows={data.closing}
        onChanged={refresh}
      />
    </div>
  );
}

function QuestionList({ title, help, kind, rows, onChanged }: {
  title: string; help: string; kind: 'statement' | 'closing'; rows: Row[]; onChanged: () => void;
}) {
  const add    = trpc.questionnaire.addStatement.useMutation({ onSuccess: () => { onChanged(); setNewEn(''); setNewAr(''); } });
  const upd    = trpc.questionnaire.updateStatement.useMutation({ onSuccess: () => { onChanged(); setEditId(null); } });
  const del    = trpc.questionnaire.deleteStatement.useMutation({ onSuccess: onChanged });
  const reorder = trpc.questionnaire.reorderStatements.useMutation({ onSuccess: onChanged });

  const closing = kind === 'closing';
  const [newEn, setNewEn]     = useState('');
  const [newAr, setNewAr]     = useState('');
  const [newType, setNewType] = useState<QType>('yesno');
  const [editId, setEditId]   = useState<number | null>(null);
  const [edit, setEdit]       = useState({ en: '', ar: '', type: 'yesno' as QType });
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const move = (id: number, dir: -1 | 1) => {
    const ids = rows.map(r => r.id);
    const i = ids.indexOf(id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate({ orderedIds: ids });
  };

  const err = add.error ?? upd.error ?? del.error ?? reorder.error;

  return (
    <section>
      <h2 className="font-bold">{title} <span className="font-normal text-slate-400 dark:text-slate-500">({rows.length})</span></h2>
      <p className={`${ui.muted} mb-3`}>{help}</p>

      <form className="mb-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={e => { e.preventDefault(); add.mutate({ en: newEn, ar: newAr, kind, ...(closing ? { type: newType } : {}), visible: true }); }}>
        <input className={ui.input} placeholder="English text" value={newEn} onChange={e => setNewEn(e.target.value)} />
        <input className={ui.input} dir="rtl" placeholder="النص بالعربية" value={newAr} onChange={e => setNewAr(e.target.value)} />
        <button type="submit" className={ui.btn} disabled={add.isPending || !newEn.trim() || !newAr.trim()}>+ Add</button>
        {closing && (
          <select className={`${ui.input} sm:col-span-3`} value={newType} aria-label="Answer buttons for the new question"
                  onChange={e => setNewType(e.target.value as QType)}>
            {(Object.keys(QTYPE_LABEL) as QType[]).map(t => <option key={t} value={t}>{QTYPE_LABEL[t]}</option>)}
          </select>
        )}
      </form>

      <ul className="flex max-h-[28rem] flex-col gap-2 overflow-y-auto">
        {rows.length === 0 && <li className={`${ui.muted} py-3 text-center`}>None.</li>}
        {rows.map((r, idx) => (
          <li key={r.id} className={`rounded-2xl border border-slate-200 dark:border-slate-800 p-3 ${r.visible ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'}`}>
            {editId === r.id ? (
              <div className="grid gap-2">
                <input className={ui.input} value={edit.en} onChange={e => setEdit(v => ({ ...v, en: e.target.value }))} />
                <input className={ui.input} dir="rtl" value={edit.ar} onChange={e => setEdit(v => ({ ...v, ar: e.target.value }))} />
                {closing && (
                  <select className={ui.input} value={edit.type} aria-label="Answer buttons"
                          onChange={e => setEdit(v => ({ ...v, type: e.target.value as QType }))}>
                    {(Object.keys(QTYPE_LABEL) as QType[]).map(t => <option key={t} value={t}>{QTYPE_LABEL[t]}</option>)}
                  </select>
                )}
                <div className="flex justify-end gap-2">
                  <button className={ui.btn} disabled={upd.isPending}
                          onClick={() => upd.mutate({ id: r.id, en: edit.en, ar: edit.ar, ...(closing ? { type: edit.type } : {}) })}>Save</button>
                  <button className={ui.btnGhost} onClick={() => setEditId(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                {closing && (
                  <div className="flex flex-col">
                    <button aria-label="Move up" disabled={idx === 0 || reorder.isPending} onClick={() => move(r.id, -1)}
                            className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▲</button>
                    <button aria-label="Move down" disabled={idx === rows.length - 1 || reorder.isPending} onClick={() => move(r.id, 1)}
                            className="h-6 w-7 rounded text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-25">▼</button>
                  </div>
                )}
                <div className={`min-w-0 flex-1 text-sm ${r.visible ? '' : 'opacity-60'}`}>
                  <p className="text-slate-900 dark:text-slate-100">{r.en}</p>
                  <p dir="rtl" className="mt-0.5 text-sky-700 dark:text-sky-400">{r.ar}</p>
                  {closing && <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{QTYPE_LABEL[r.type as QType] ?? r.type}</p>}
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  {confirmId === r.id ? (
                    <>
                      <span className="text-xs text-slate-500 dark:text-slate-400">Remove?</span>
                      <button className={ui.btnDanger} onClick={() => { del.mutate({ id: r.id }); setConfirmId(null); }}>Yes</button>
                      <button className={ui.btnGhost} onClick={() => setConfirmId(null)}>No</button>
                    </>
                  ) : (
                    <>
                      <button className={ui.btnGhost} disabled={upd.isPending}
                              onClick={() => upd.mutate({ id: r.id, visible: !r.visible })}>{r.visible ? 'Hide' : 'Show'}</button>
                      <button className={ui.btnGhost}
                              onClick={() => { setEditId(r.id); setEdit({ en: r.en, ar: r.ar, type: r.type as QType }); }}>Edit</button>
                      <button className={ui.btnDanger} onClick={() => setConfirmId(r.id)}>Remove</button>
                    </>
                  )}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      {err && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errText(err)}</p>}
    </section>
  );
}
